import ExploreEmptyState from "@modules/new-explore/empty-state";
import ExploreSkeletonLoading from "@modules/new-explore/loading";

import ExploreSidebar from "@modules/new-explore/sidebar";
import ExploreMobileHeader from "@modules/new-explore/sidebar/mobile/mobile-header";
import SortBox from "@modules/new-explore/sort-box";
import { separateMoneyNumbers, toFarsiNumber } from "@utils/digits";
import NotFound from "pages/404";

import { EXPLORE_SLUG } from "types/api/explore";
import {
  NewExploreInstructor,
  NewExploreLanding,
  NewExploreProduct,
} from "types/api/new-explore";
import classNames from "classnames";
import { useExplorePage } from "./hooks/useExplore";
import { exploreTitle } from "constants/new-explore/valid-filters";
import ExploreCard from "@modules/new-explore/explore-card";
import ExploreLandingCard from "@modules/new-explore/landing-explore-card";
import { useRouter } from "next/router";
import { ICard, ILandingCard } from "@shared/components/card/types";
import TeacherCard from "@modules/new-explore/teacher-card";
import Pagination from "legacy/elements/Pagination";

const getCardComponent = (
  props:
    | Omit<ICard, "variant">
    | NewExploreInstructor
    | Omit<ILandingCard, "variant">,
  slug: EXPLORE_SLUG
) => {
  if (slug === EXPLORE_SLUG.INSTRUCTOR) {
    return <TeacherCard {...(props as NewExploreInstructor)} />;
  }

  if (slug === EXPLORE_SLUG.LANDING) {
    return <ExploreLandingCard {...(props as ILandingCard)} />;
  }
  return <ExploreCard {...(props as ICard)} />;
};

const NewExplore: React.FC = () => {
  const { query } = useRouter();
  const slug =
    typeof query.slug === "string"
      ? (query.slug as EXPLORE_SLUG)
      : EXPLORE_SLUG.COURSE;
  const term = query?.term;

  const {
    paginationData,
    exploreError,
    productData,
    getProductCardProps,
    onPageChange,
    isLoading,
  } = useExplorePage();

  if (exploreError?.error?.code === 404) return <NotFound />;

  const isEmpty = !isLoading && productData?.length === 0;

  return (
    <div
      className="px-0 w-full mx-auto  xl:max-w-[1232px] lg:px-4"
      id="explore"
    >
      <ExploreMobileHeader />
      <div className="flex items-start lg:py-10 relative">
        <ExploreSidebar />
        <div className="w-full lg:w-[calc(100%-282px)]  lg:basis-[calc(100%-282px)] grow-0 shrink-0 lg:pr-6">
          <div className="hidden lg:block">
            <SortBox count={paginationData?.total_items || 0} />
          </div>
          {isLoading ? (
            <>
              <div className="sm:hidden block">
                <ExploreSkeletonLoading
                  isMobile
                  slug={slug}
                  skeletonCount={6}
                />
              </div>
              <div className="hidden sm:block">
                <ExploreSkeletonLoading
                  isMobile={false}
                  slug={slug}
                  skeletonCount={6}
                />
              </div>
            </>
          ) : isEmpty ? (
            <ExploreEmptyState />
          ) : (
            <>
              <div className="flex lg:px-0 px-4 justify-between items-center lg:hidden py-5  border-t-8 border-zinc-100">
                <p className="font-medium text-xs text-fds-tertiary leading-4">
                  {term
                    ? `نتایج مرتبط با ${
                        Array.isArray(term) ? term.join(", ") : term
                      }`
                    : ""}
                </p>

                <p className="font-medium text-xs text-fds-tertiary leading-4">
                  {separateMoneyNumbers(
                    toFarsiNumber(paginationData?.total_items)
                  )}{" "}
                  {exploreTitle[slug]}
                </p>
              </div>
              <div className=" xl:gap-6  overflow-hidden sm:gap-x-4     gap-x-1.5  gap-y-2  lg:pb-9   flex  flex-wrap px-4     lg:px-0 ">
                {productData?.map(
                  (
                    item:
                      | NewExploreProduct
                      | NewExploreLanding
                      | NewExploreInstructor
                  ) => {
                    const propsData = getProductCardProps(item);
                    return (
                      <div
                        key={item.id}
                        className={classNames(
                          "sm:w-fds-282 ",
                          slug === EXPLORE_SLUG.INSTRUCTOR ? "w-40" : "w-full"
                        )}
                      >
                        {getCardComponent(propsData, slug)}
                      </div>
                    );
                  }
                )}
              </div>
              {paginationData && paginationData?.total_pages !== 1 ? (
                <>
                  <div className="sm:mt-14 hidden sm:block mt-5">
                    <Pagination
                      currentPage={paginationData.page}
                      onPageChange={onPageChange}
                      pageItemsToShow={5}
                      totalPages={paginationData?.total_pages || 0}
                    />
                  </div>
                  <div className="sm:mt-14 block sm:hidden mt-5">
                    <Pagination
                      currentPage={paginationData.page}
                      onPageChange={onPageChange}
                      pageItemsToShow={3}
                      totalPages={paginationData?.total_pages || 0}
                    />
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewExplore;
